export type ContentBlock = {
  type: "p" | "h3" | "quote";
  text: string;
};

export type Article = {
  id: string;
  category: string;
  title: string;
  excerpt: string;
  cover: string;
  author: string;
  date: string;
  readMinutes: number;
  views: string;
  tags: string[];
  featured?: boolean;
  content: ContentBlock[];
};

const p = (text: string): ContentBlock => ({ type: "p", text });
const h3 = (text: string): ContentBlock => ({ type: "h3", text });
const quote = (text: string): ContentBlock => ({ type: "quote", text });

export const articles: Article[] = [
  {
    id: "shenxingye-contract-question",
    category: "exclusive",
    title: "独家：沈星野新约走向反转，创作话语权成谈判核心",
    excerpt:
      "经过近三周的拉锯，沈星野与老东家的续约谈判出现关键转折。多方信源向我们确认，争议焦点并非分成比例，而是作品最终剪辑权归谁。",
    cover:
      "https://readdy.ai/api/search-image?query=dramatic%20crimson%20spotlight%20on%20empty%20theater%20stage%2C%20abstract%20artistic%20editorial%20image%2C%20warm%20amber%20accents%2C%20film%20grain%2C%20moody%20cinematic%20lighting%2C%20minimal%20composition&width=1200&height=800&seq=bl-art-01&orientation=landscape",
    author: "本刊调查组",
    date: "2026-09-09",
    readMinutes: 7,
    views: "18.6 万",
    tags: ["沈星野", "独家猛料", "经纪合约"],
    featured: true,
    content: [
      p("谈判桌上的文件被反复推回原位。据两位接近合作方的信源描述，过去二十天里，双方围绕一份为期三年的新合约进行了至少四轮面对面沟通，但真正的分歧从来不写在分成数字上。"),
      h3("争议其实只有一个词"),
      p("「剪辑权」——这是关键词。一位参与前期沟通的制片告诉我们，沈星野方面坚持在剧集与电影项目中保留对成片剪辑的建议权与确认权，而这在此前的合作框架里从未被写进条款。"),
      quote("钱可以谈，署名也可以谈，但故事最后长成什么样，这次我想自己说了算。"),
      p("老东家并非不能接受，顾虑在于流程。内部评估认为，把确认权前移会让后期周期平均延长两到三周，而平台排播档期通常很难为单个项目让步。"),
      h3("为什么这次不同"),
      p("和以往几次续约相比，这次的变化在于外部选项明显变多。近半年里至少有四家制作公司通过中间人递过橄榄枝，其中两家愿意直接把最终剪辑确认写进主合同。选择变多，谈判姿态自然也就变了。"),
      p("截至发稿，双方均未对具体条款作出回应。我们会在拿到进一步材料后继续更新这篇报道。"),
    ],
  },
  {
    id: "mist-harbor-post-production",
    category: "investigation",
    title: "内幕调查：一部剧的四十天停摆，钱到底卡在了哪一环",
    excerpt:
      "《雾港》原定暑期上线，却在后期阶段整整停摆四十天。我们梳理了资金流、承制方与平台三方的时间线，试图找到那个真正按下暂停的人。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20foggy%20harbor%20silhouette%20at%20dusk%2C%20deep%20umber%20and%20amber%20color%20palette%2C%20artistic%20editorial%20photography%2C%20soft%20grain%2C%20layered%20atmospheric%20composition&width=1200&height=800&seq=bl-art-02&orientation=landscape",
    author: "陈屿",
    date: "2026-09-08",
    readMinutes: 11,
    views: "26.3 万",
    tags: ["内幕调查", "影视工业", "资金链"],
    featured: true,
    content: [
      p("停摆的第一天没有人当回事。剧组群里只发了一条通知，说后期机房需要调整排期。直到第十天，调色、混音、特效三条线全部停摆，合作方才意识到问题不在排期。"),
      h3("三条线，一个共同的堵点"),
      p("我们比对了三家供应商的结算记录：调色公司的尾款延迟了十九天，混音团队延迟了二十三天，特效公司的第三期进度款则完全没有到账。三条线的付款方都是同一家承制公司。"),
      quote("不是没钱，是钱进不来也出不去——账户被上游卡住了，我们连发票都开不了。"),
      p("一位财务岗位的受访者解释了其中机制：项目采用分段结算，平台方先打款到承制公司共管账户，再按节点释放。若某一节点验收未过，后续款项会被冻结在账户里，形成连锁延迟。"),
      h3("那个没过的节点"),
      p("关键节点出现在特效回看环节。平台方以「成片质感与前期样片差距过大」为由暂缓验收，要求补充两版测试。承制方认为这是变相压价，双方就此僵持。"),
      p("从结果看，四十天的停摆让这部剧错过了整个暑期档，最终改档到十月。对观众而言只是一次改档通知，但对链条上的每一环，都是一次真实的现金流考验。"),
    ],
  },
  {
    id: "linwantang-variety-edit",
    category: "gossip",
    title: "微八卦：林晚棠在综艺里的三次沉默，藏着什么信息",
    excerpt:
      "最新一期综艺播出后，林晚棠三段明显的「接不上话」被观众反复截图。我们把完整素材重新看了一遍，发现沉默出现的位置很有讲究。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20warm%20beige%20stage%20curtain%20with%20soft%20amber%20light%20streaks%2C%20artistic%20editorial%20texture%2C%20vintage%20film%20aesthetic%2C%20minimal%20elegant%20composition&width=1200&height=800&seq=bl-art-03&orientation=landscape",
    author: "苏见月",
    date: "2026-09-07",
    readMinutes: 5,
    views: "31.2 万",
    tags: ["微八卦", "综艺", "林晚棠"],
    content: [
      p("剪辑是综艺的第二支笔。同一段素材，切在不同位置，观众读到的情绪完全不同。林晚棠这三次沉默之所以被放大，恰恰因为它们都发生在话题最敏感的位置。"),
      h3("第一次沉默：自我介绍之后的空白"),
      p("正常情况下，嘉宾自我介绍结束会立刻接一个轻松的话题暖场。但这一期里，镜头在林晚棠脸上停留了将近四秒，随后直接切到另一位嘉宾。这个空白不像是剪辑失误，更像是有内容的段落被拿掉了。"),
      h3("第二次与第三次"),
      p("第二次出现在谈论合作搭档的环节，第三次则在收官总结时。三次的共同点是：都发生在需要评价他人或点评行业现状的时刻。"),
      quote("她不是没话说，是不想在那个场合说。"),
      p("一位参与过同类节目录制的编导告诉我们，嘉宾往往会在录制前就与团队约定「不聊哪些话题」，后期会主动避开这些段落，形成我们看到的沉默。"),
      p("这当然不构成任何实质判断。但对追内容的人来说，沉默本身也是一种信息——它提醒我们，镜头之外还有一整个未被剪进去的房间。"),
    ],
  },
  {
    id: "shanhai-shiyi-flash",
    category: "flash",
    title: "首发快讯：《山海拾遗》官宣开机，主创名单确认",
    excerpt:
      "筹备一年多的《山海拾遗》今日正式官宣开机，导演与两位主演人选落定，拍摄周期约四个月。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20mountain%20ridge%20silhouette%20in%20warm%20amber%20sunrise%2C%20deep%20umber%20tones%2C%20artistic%20editorial%20painterly%20texture%2C%20minimal%20elegant%20composition&width=1200&height=800&seq=bl-art-04&orientation=landscape",
    author: "快讯编辑",
    date: "2026-09-07",
    readMinutes: 3,
    views: "12.4 万",
    tags: ["首发快讯", "山海拾遗", "开机"],
    content: [
      p("《山海拾遗》今日官宣开机，主创阵容同步公开。项目自去年启动筹备，先后经历两轮剧本打磨与一次档期调整。"),
      h3("要点速览"),
      p("导演：顾南枝；主演：陆时序、江雪见；拍摄周期约四个月；取景地跨三地。官方尚未公布上线平台与具体档期。"),
      p("据接近项目的人士透露，本轮拍摄将优先完成实景部分，棚内与特效段落集中在后两个月推进，以便为后期留出更充裕的时间。"),
      p("我们会在发布会后补充更完整的主创访谈内容。"),
    ],
  },
  {
    id: "agency-talent-flow",
    category: "depth",
    title: "深度长文：这一年，经纪公司为什么开始悄悄「放人」",
    excerpt:
      "从绑定到松绑，头部经纪公司与艺人之间的关系在过去十二个月里发生了微妙而普遍的变化。我们访谈了七位从业者，试图解释这种集体转向。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20interlocking%20warm%20brown%20geometric%20rings%2C%20editorial%20conceptual%20image%2C%20amber%20highlights%2C%20soft%20grain%2C%20minimal%20sophisticated%20composition&width=1200&height=800&seq=bl-art-05&orientation=landscape",
    author: "周叙白",
    date: "2026-09-06",
    readMinutes: 14,
    views: "9.8 万",
    tags: ["深度长文", "经纪行业", "趋势"],
    content: [
      p("如果你把近一年里公开的合约变动做成一张表，会发现一个不太显眼但相当一致的走向：长约变少了，短约与项目制合作变多了，续约时「解绑」的案例明显增加。"),
      h3("一句话概括：风险在被重新分配"),
      p("过去行业依赖长约锁定稀缺的「人」，回报来自长期分成的复利。但当内容周期变短、项目的确定性下降，长约对双方都成了负担——公司要背固定成本，艺人要背窗口期。"),
      quote("以前是谁先把人签下来谁赢，现在是谁先把结构做轻谁活得久。"),
      p("七位受访者里，有五位提到了同一个关键词：轻资产。也就是说，公司不再需要把艺人当作必须长期持有的资产，而是当作可以按项目组合的资源。"),
      h3("这不完全是坏事"),
      p("松绑降低了新人的入门门槛，也让成熟的创作者有更多试错空间。代价是稳定性下降，宣发、商务、法务这些后台能力需要重新设计，否则会变成一盘散沙。"),
      p("接下来的半年，会是观察这套新结构能否跑通的关键窗口。"),
    ],
  },
  {
    id: "gushizhou-budget",
    category: "exclusive",
    title: "独家：周叙白新片预算翻倍，钱花在了看不见的地方",
    excerpt:
      "周叙白新项目立项时预算约为八千万，最新版本已接近一亿七千万。多出来的钱没有变成场面，而是进了另一个科目。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20crimson%20and%20amber%20paper%20texture%20layers%2C%20editorial%20artistic%20composition%2C%20vintage%20print%20grain%2C%20minimal%20sophisticated%20visual&width=1200&height=800&seq=bl-art-06&orientation=landscape",
    author: "本刊调查组",
    date: "2026-09-05",
    readMinutes: 6,
    views: "14.1 万",
    tags: ["独家猛料", "周叙白", "电影预算"],
    content: [
      p("预算表上最容易被误解的一栏，往往不是演员片酬，而是「时间」。周叙白新项目的最新预算几乎翻倍，但看过方案的人说，多出来的钱几乎全部买了一样东西：更长的制作周期。"),
      h3("钱去哪了"),
      p("较上一版方案，拍摄周期从七十五天延长到一百零五天，后期从五个月延长到九个月。人力、场地、设备在更长时间里持续产生成本，这才是翻倍的主要来源。"),
      p("一位参与预算复核的人士补充，项目还额外预留了约百分之八的「返工准备金」，用于应对实拍后改写方案的情况。"),
      quote("这次宁可慢一点，也不想再靠后期救故事。"),
      p("在当下追求快回报的环境里，为一个项目主动放慢节奏并不常见。它能不能换来更好的成片，要等到上映才知道。"),
    ],
  },
  {
    id: "village-ip-heated",
    category: "investigation",
    title: "内幕调查：三部「同题剧」同期开拍，谁在赌同一张牌",
    excerpt:
      "题材撞车不稀奇，但三部同题材剧集在同一个月官宣开拍，背后是一次明显的选题押注。我们复盘了它们的立项时间线。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20overlapping%20warm%20umber%20and%20gold%20film%20frames%2C%20editorial%20conceptual%20texture%2C%20soft%20grain%2C%20minimal%20artistic%20composition&width=1200&height=800&seq=bl-art-07&orientation=landscape",
    author: "陈屿",
    date: "2026-09-04",
    readMinutes: 9,
    views: "7.6 万",
    tags: ["内幕调查", "选题趋势", "剧集市场"],
    content: [
      p("立项这件事有一个残酷之处：所有决策都发生在结果之前，而结果往往要一两年后才浮现。三部同题剧的同期启动，正是这种时间差的典型样本。"),
      h3("时间线高度重合"),
      p("三部剧的立项备案集中在去年最后两个月，剧本初稿完成时间相差不到三周。这通常意味着一件事：市场团队在同一个季度得出了相似结论。"),
      quote("当三家公司同时看到一个方向，那个方向大概率已经开始变贵了。"),
      p("成本抬升最先体现在取景地与美术资源上。一位制片主任提到，同题材项目扎堆时，热门实景地的档期会被提前半年订满，价格水涨船高。"),
      h3("谁会赢，谁会白忙"),
      p("最终取决于谁能在同质化里做出差异。历史经验是，三部里通常只有一到两部能真正跑出来，剩下的一部会成为行业内部复盘的素材。"),
    ],
  },
  {
    id: "jiangxuejian-interview",
    category: "gossip",
    title: "微八卦：江雪见那句「不方便说」，到底在回避什么",
    excerpt:
      "上周的群访里，江雪见被问到与新合作的感受时，用「不方便说」带过了。这句话之后的表情变化，比答案本身更有意思。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20soft%20amber%20microphone%20silhouette%20on%20warm%20beige%20backdrop%2C%20editorial%20minimal%20artistic%20image%2C%20gentle%20grain%2C%20elegant%20composition&width=1200&height=800&seq=bl-art-08&orientation=landscape",
    author: "苏见月",
    date: "2026-09-03",
    readMinutes: 4,
    views: "22.9 万",
    tags: ["微八卦", "江雪见", "采访"],
    content: [
      p("采访里最难处理的不是尖锐问题，而是你明知道对方不会回答，却必须问出口的那种。江雪见这次遇到的，就是后者。"),
      h3("答案之前的那个停顿"),
      p("被问到与新搭档的合作感受时，她先笑了一下，然后说「这个现在真的不方便说」。停顿很短，但配合随后一个把手放回膝盖的动作，情绪是收着的。"),
      quote("不方便说不等于有矛盾，也可能只是还没到能说的时候。"),
      p("熟悉她的记者提到，江雪见在表达保留意见时习惯用「现在」这个词，把判断限定在时间范围内。这是一种礼貌，也是一种保护。"),
      p("所以与其去猜内容，不如记住这个细节：在结果揭晓前保持沉默，本身就是这个行业最稳妥的表达方式之一。"),
    ],
  },
  {
    id: "short-drama-regulation",
    category: "depth",
    title: "深度长文：短剧狂奔两年后，第一次遇到真正的天花板",
    excerpt:
      "增长速度放缓、投放成本上涨、内容同质化加剧——短剧行业在狂奔两年后，撞上了它的第一堵墙。这是一次关于模式的复盘。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20vertical%20amber%20light%20bars%20on%20deep%20umber%20background%2C%20editorial%20artistic%20texture%2C%20film%20grain%2C%20minimal%20modern%20composition&width=1200&height=800&seq=bl-art-09&orientation=landscape",
    author: "周叙白",
    date: "2026-09-02",
    readMinutes: 12,
    views: "6.3 万",
    tags: ["深度长文", "短剧", "行业复盘"],
    content: [
      p("任何依赖投放获客的生意，最终都会面对同一个数学问题：当获客成本逼近单个用户的价值上限，增长就会自动停下。短剧行业刚刚走到这道题前。"),
      h3("三个同时发生的信号"),
      p("其一，头部投手反馈的获客成本较去年同期上涨明显；其二，爆款的生命周期在缩短，从两周压缩到不足一周；其三，平台开始把流量更多导向自制内容。"),
      quote("以前是内容追着流量跑，现在是流量开始挑内容。"),
      p("这并不意味着行业萎缩，而是意味着分工要变。靠单一模板快速复制红利的阶段结束了，接下来的竞争会回到剧本、表演与叙事效率上。"),
      h3("谁能活下来"),
      p("大概率是两类团队：一类能把成本压到极致并稳定产出；另一类能在有限预算里做出真正让人记住的角色。中间那些既不够便宜也不够特别的，会被最先淘汰。"),
      p("对观众来说，这反而是个好消息——当流量不再廉价，内容才不得不变得认真。"),
    ],
  },
  {
    id: "guzhi-ending-reshoot",
    category: "flash",
    title: "首发快讯：《长夜未明》补拍结局，档期顺延两周",
    excerpt:
      "因结局段落反馈调整，《长夜未明》启动小范围补拍，原定上线时间顺延约两周。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20night%20sky%20with%20amber%20horizon%20glow%20over%20dark%20umber%20landscape%2C%20editorial%20artistic%20image%2C%20soft%20grain%2C%20minimal%20cinematic%20composition&width=1200&height=800&seq=bl-art-10&orientation=landscape",
    author: "快讯编辑",
    date: "2026-09-01",
    readMinutes: 3,
    views: "10.7 万",
    tags: ["首发快讯", "长夜未明", "补拍"],
    content: [
      p("《长夜未明》确认启动补拍，范围集中在结局段落的若干场戏，预计补拍周期一周左右，上线时间顺延约两周。"),
      h3("要点速览"),
      p("补拍原因：结局段落观众测试反馈存在分歧；涉及演员已协调档期回归；成片已进入新一轮后期。"),
      p("业内人士指出，为结局补拍在近两年逐渐常态化，制作方更愿意在上线前把争议点处理掉，而不是上线后再面对口碑波动。"),
    ],
  },
  {
    id: "luxu-bai-ai-tools",
    category: "exclusive",
    title: "独家：白鹿鸣工作室引入 AI 剪辑助手，内部反应两极",
    excerpt:
      "一份内部通知显示，白鹿鸣工作室开始试点 AI 剪辑辅助工具。有人视为效率革命，也有人担心创意被标准化。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20glowing%20amber%20data%20lines%20on%20warm%20dark%20umber%20backdrop%2C%20editorial%20conceptual%20texture%2C%20soft%20grain%2C%20minimal%20artistic%20composition&width=1200&height=800&seq=bl-art-11&orientation=landscape",
    author: "本刊调查组",
    date: "2026-08-30",
    readMinutes: 6,
    views: "11.2 万",
    tags: ["独家猛料", "AI 工具", "后期制作"],
    content: [
      p("通知发在内部系统里，措辞克制：试点周期三个月，参与自愿，工具主要用于粗剪排序与素材检索。但对一个高度依赖手感的岗位来说，任何自动化都值得多看两眼。"),
      h3("支持的一派怎么说"),
      p("粗剪阶段最耗时的从来不是创意，而是把几百小时的素材按场次和情绪归类。一位剪辑助理估算，工具若能把这一步压缩一半，一个项目就能省出近一周。"),
      quote("它替掉的是找素材的力气，不是讲故事的手。"),
      h3("担忧的一派怎么看"),
      p("另一部分人担心，长期依赖推荐排序会让大家剪出越来越像的节奏。当所有人用同一套推荐逻辑，作品之间的差异会被悄悄抹平。"),
      p("工作室目前的态度是观察而非推广。试点结果会在三个月后评估，届时是否扩大范围仍未确定。"),
    ],
  },
  {
    id: "fan-circle-shift",
    category: "gossip",
    title: "微八卦：应援方式变了，粉丝团开始把钱花在「看不见」的地方",
    excerpt:
      "从地铁灯箱到公益项目，头部粉丝团的应援预算表正在悄悄改写。这份变化比明星本人更能说明行业风向。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20warm%20amber%20and%20beige%20paper%20lanterns%20softly%20glowing%2C%20editorial%20artistic%20texture%2C%20vintage%20film%20grain%2C%20minimal%20elegant%20composition&width=1200&height=800&seq=bl-art-12&orientation=landscape",
    author: "林晚棠",
    date: "2026-08-28",
    readMinutes: 5,
    views: "8.9 万",
    tags: ["微八卦", "粉丝文化", "应援"],
    content: [
      p("过去几年，衡量一个粉丝团实力的标准很直接：能买下多大的广告位。但这个标准正在被替换，新的标准是「能不能被看见的同时也被认可」。"),
      h3("从铺量到做项目"),
      p("两份粉丝团内部预算表显示，户外硬广的占比从早期的六成下降到不足三成，取而代之的是公益项目、创作类内容与文化衍生品。"),
      quote("灯箱三天就撤了，但一个能留下来的项目会被记住很久。"),
      p("一位粉丝团负责人解释，这种转变一半是主动，一半是被动的：硬广成本上升、监管趋于规范，同时平台也不再把单纯的曝光量当作唯一指标。"),
      p("结果是一种更克制、更长期的应援方式。它不一定更热闹，但确实更像一种表达。"),
    ],
  },
];

export const getArticle = (id: string): Article | undefined =>
  articles.find((item) => item.id === id);

export const getArticlesByCategory = (slug: string): Article[] =>
  articles.filter((item) => item.category === slug);

export const getArticlesByIds = (ids: string[]): Article[] =>
  articles.filter((item) => ids.includes(item.id));